import NiceModal from '@ebay/nice-modal-react'
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import LogoutIcon from '@mui/icons-material/Logout'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Typography
} from '@mui/material'
import {
  CompanyUsersRelation,
  CompanyUserWithCompanyAndRole
} from '@shared/types/companies.types'
import { Role } from '@shared/types/roles.types'
import { MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { getAvatarCompanyName } from '../utils/company.utils'
import { AbandonCompanyModal } from './modals/AbandonCompanyModal'
import { DeleteCompanyModal } from './modals/DeleteCompanyModal'
import RoleChip from './RoleChip'

const avatarStyles = {
  width: '3.5rem',
  height: '3.5rem',
  borderRadius: '.25rem'
}

export default function CompanyPaper({
  company
}: {
  company: CompanyUserWithCompanyAndRole
}) {
  const { t } = useTranslation()
  const [menuState, setMenuState] = useState<{
    anchorEl: HTMLElement | null
    role: Role | null
  }>({
    anchorEl: null,
    role: null
  })
  const menuOpen = Boolean(menuState.anchorEl)

  const handleMenuClick = (event: MouseEvent<HTMLElement>, role: Role) =>
    setMenuState({ anchorEl: event.currentTarget, role })

  const handleCloseMenu = () => setMenuState({ anchorEl: null, role: null })

  const relationLabels = {
    [CompanyUsersRelation.CLIENT]: t('relation.client'),
    [CompanyUsersRelation.EMPLOYEE]: t('relation.employee'),
    [CompanyUsersRelation.PROVIDER]: t('relation.provider')
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '22rem',
        borderRadius: '1rem',
        backgroundColor: '#2A2D30',
        gap: '.5rem',
        padding: '1rem'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Avatar
            variant='rounded'
            src={company.company.logoUri || undefined}
            sx={avatarStyles}
          >
            {!company.company.logoUri &&
              getAvatarCompanyName(company.company.name)}
          </Avatar>
          <Box>
            <Typography variant='body2' fontWeight='bold'>
              {company.company.name}
            </Typography>
            <Box sx={{ display: 'flex', gap: '0.62rem', mt: '0.25rem' }}>
              <RoleChip role={company.role} />
              {company.relation && (
                <Chip label={relationLabels[company.relation]} />
              )}
            </Box>
          </Box>
        </Box>
        <IconButton
          onClick={e => handleMenuClick(e, company.role)}
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.07)'
          }}
        >
          <MoreHorizIcon />
        </IconButton>
      </Box>

      <Button
        variant='outlined'
        color='primary'
        component={Link}
        to={`/companies/${company.company.id}/workspaces`}
        sx={{
          padding: '0.375rem 1rem',
          textTransform: 'none',
          backgroundColor: 'rgba(178, 223, 219, 0.10)'
        }}
      >
        {t('common.enter')}
      </Button>

      <Menu
        anchorEl={menuState.anchorEl}
        open={menuOpen}
        onClose={handleCloseMenu}
      >
        <MenuList>
          {company.role.fullAccess && (
            <MenuItem
              component={Link}
              to={`/companies/${company.company.id}/profile`}
              onClick={handleCloseMenu}
            >
              <ListItemIcon>
                <CreateOutlinedIcon fontSize='small' />
              </ListItemIcon>
              <ListItemText>{t('common.edit')}</ListItemText>
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              NiceModal.show(AbandonCompanyModal, {
                companyId: company.company.id,
                companyName: company.company.name
              })
              handleCloseMenu()
            }}
          >
            <ListItemIcon>
              <LogoutIcon fontSize='small' />
            </ListItemIcon>
            <ListItemText>{t('common.abandon')}</ListItemText>
          </MenuItem>
          {company.role.fullAccess && (
            <MenuItem
              onClick={() => {
                handleCloseMenu()
                NiceModal.show(DeleteCompanyModal, {
                  companyId: company.company.id,
                  companyName: company.company.name
                })
              }}
            >
              <ListItemIcon>
                <DeleteOutlineOutlinedIcon fontSize='small' color='error' />
              </ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>
                {t('common.delete')}
              </ListItemText>
            </MenuItem>
          )}
        </MenuList>
      </Menu>
    </Box>
  )
}
